<template>
  <div class="elevation-4">
    <shared-map
      :drawing-tool-cursor="drawingToolCursor"
      @l-map-ready="setLMap"
      @mousedown="handleMouseDown"
      @mouseup="handleMouseUp"
      @mousemove="handleMapMouseMove"
    >
      <template #map>
        <l-rectangle
          v-if="gridStore.leafletBbox"
          data-testid="grid-map-bbox"
          :fill-color="color"
          :fill-opacity="0.35"
          :color="color"
          :stroke="false"
          :bounds="gridStore.leafletBbox"
          :interactive="false"
        />
        <l-rectangle
          v-if="outerBounds"
          data-testid="grid-map-bbox-selected"
          :fill="false"
          :color="color"
          :weight="3"
          :bounds="outerBounds"
          :interactive="false"
        />
      </template>
      <template #tools>
        <v-tooltip
          :text="t('raster.tooltip.reset_bbox')"
          bottom
          :open-delay="200"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              :class="'ml-4 delete-button zamg-button-outline-primary'"
              data-testid="map-bbox-delete"
              variant="outlined"
              elevation="2"
              @click="gridStore.resetBbox()"
            >
              <v-icon color="primary">
                mdi-delete-outline
              </v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <v-tooltip
          :text="t('raster.tooltip.select_bbox_on_map')"
          bottom
          :open-delay="200"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              class="ml-4 zamg-button-outline-primary"
              :class="[toolIsActive ? 'active-button' : '']"
              min-width="30px"
              width="30px"
              height="32px"
              data-testid="grid-map-marker"
              @click="toggleToolIsActive"
            >
              <v-icon
                icon="custom:zamgRectangle"
                color="primary"
              />
            </v-btn>
          </template>
        </v-tooltip>
      </template>
    </shared-map>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick } from "vue";
import L, { LatLng, LatLngBounds, LeafletMouseEvent } from "leaflet";
import type { LMap } from "@vue-leaflet/vue-leaflet";
import type { Map as LeafletMap } from 'leaflet';
import SharedMap from "@/components/common/map/Map.vue";
import GridStore from "@/stores/grid/grid-module";
import RasterDatasetStore from "@/stores/dataset/raster-dataset-module";
import { colors } from "@/plugins/vuetify";
import { COORD_DECIMAL_PRECISION } from "@/util/round";
import { LRectangle } from "@vue-leaflet/vue-leaflet";
import { useI18n } from "vue-i18n";

interface LMapWithMapObject extends InstanceType<typeof LMap> {
  mapObject: L.Map
}

export default defineComponent ({
  name: 'GridMap',
  components: {
    SharedMap,
    LRectangle,
  },
  props: {
    visible: { type: Boolean, required: true },
  },
  setup(props) {
    const { t } = useI18n();

    const gridStore = GridStore;
    const rasterDatasetStore = RasterDatasetStore;

    const lMap = ref<LMapWithMapObject | null>(null);
    const leafletMap = computed<LeafletMap | null>(() => lMap.value?.mapObject ?? null);

    const color = ref(colors.primary);
    const toolIsActive = ref(false);
    const drawIsActive = ref(false);
    const anchorStart = ref<LatLng | null>(null);

    const setLMap = (map: LMapWithMapObject) => {
      lMap.value = map;
    };

    // TODO: event listener logic is duplicated from TimeseriesMap
    watch(toolIsActive, (newVal) => {
      if (newVal) {
        document.addEventListener("keyup", cancelToolActive);
        document.addEventListener("mouseup", cancelToolActive);
        document.addEventListener("contextmenu", disableContextMenu);
      } else {
        document.removeEventListener("keyup", cancelToolActive);
        document.removeEventListener("mouseup", cancelToolActive);
        document.removeEventListener("contextmenu", disableContextMenu);
      }
    });

    const cancelToolActive = (event: KeyboardEvent | MouseEvent) => {
      if (
        ("key" in event && event.key === "Escape") ||
        ("button" in event && event.button === 2)
      ) {
        toolIsActive.value = false;
        event.preventDefault();
      }
    };

    const disableContextMenu = (event: Event) => {
      event.preventDefault();
    };

    // When visibility is toggled, invalidate map to reload tiles
    watch(
      () => props.visible,
      async (visible) => {
        if (visible && leafletMap.value) {
          await nextTick();
          leafletMap.value.invalidateSize();
        }
      }
    );

    // When leaflet map exists, invalidate it to reload tiles
    watch(leafletMap, (newMap) => {
      newMap?.invalidateSize()
    });

    // Watch gridStore bbox changes
    const watchIsValidBbox = () => {
      // due to the nature of the gridStore it is important that the if statements are in that order
      // without that, weird zooms can happen
      if (toolIsActive.value) return;
      const bbox = gridStore.leafletBbox;
      if (bbox === null) {
        if (leafletMap.value && outerBounds.value)
          leafletMap.value.fitBounds(outerBounds.value);
        return;
      }
      if (!gridStore.isValidBbox) return;
      if (leafletMap.value) leafletMap.value.fitBounds(bbox);
    };

    watch(
      [
        () => gridStore.bboxSouth,
        () => gridStore.bboxWest,
        () => gridStore.bboxNorth,
        () => gridStore.bboxEast,
      ],
      watchIsValidBbox
    );

    const toggleToolIsActive = () => {
      toolIsActive.value = !toolIsActive.value;
      if (toolIsActive.value) anchorStart.value = null;
    };

    const drawingToolCursor = computed<'crosshair' | 'none' | ''>(() => {
      return toolIsActive.value ? 'crosshair' : '';
    });

    const roundLatLng = (latLng: LatLng): LatLng =>
      new LatLng(
        parseFloat(latLng.lat.toFixed(COORD_DECIMAL_PRECISION)),
        parseFloat(latLng.lng.toFixed(COORD_DECIMAL_PRECISION))
      );

    const updateBBox = (endAnchor: LatLng): void => {
      if (anchorStart.value) {
        gridStore.updateBbox(new LatLngBounds(endAnchor, anchorStart.value));
      }
    };

    const handleMouseDown = (event: LeafletMouseEvent): void => {
      if (event.originalEvent.button !== 0) return; // left click only
      if (toolIsActive.value) {
        event.originalEvent.stopPropagation();
        leafletMap.value?.dragging.disable();
        drawIsActive.value = true;
        anchorStart.value = roundLatLng(keepLatLongInsideBounds(event.latlng));
      }
    };

    const handleMapMouseMove = (event: LeafletMouseEvent): void => {
      if (toolIsActive.value && drawIsActive.value) {
        event.originalEvent.stopPropagation();
        updateBBox(roundLatLng(keepLatLongInsideBounds(event.latlng)));
      }
    };

    const handleMouseUp = (event: LeafletMouseEvent): void => {
      if (event.originalEvent.button !== 0) return; // left click only
      if (drawIsActive.value) {
        leafletMap.value?.dragging.enable();
        toolIsActive.value = false;
        drawIsActive.value = false;
      }
    };

    const keepLatLongInsideBounds = (latlng: LatLng): LatLng => {
      if (!outerBounds.value) return latlng;
      const [south, north, east, west] = [
        outerBounds.value.getSouth(),
        outerBounds.value.getNorth(),
        outerBounds.value.getEast(),
        outerBounds.value.getWest(),
      ];
      const lat = latlng.lat < south ? south : latlng.lat > north ? north : latlng.lat;
      const lng = latlng.lng < west ? west : latlng.lng > east ? east : latlng.lng;
      return new LatLng(lat, lng);
    };

    const outerBounds = computed<LatLngBounds | null>(() => {
      if (rasterDatasetStore.metadata) {
        const bounds = rasterDatasetStore.metadata.bboxOuter;
        // the latlng of the mouse event is set to be inside these bounds. To account for rounding errors in
        // this.roundLatLng(), the bounds have to be made slightly smaller
        return new LatLngBounds(
          [
            bounds.south + Math.pow(10, -COORD_DECIMAL_PRECISION),
            bounds.west + Math.pow(10, -COORD_DECIMAL_PRECISION),
          ],
          [
            bounds.north - Math.pow(10, -COORD_DECIMAL_PRECISION),
            bounds.east - Math.pow(10, -COORD_DECIMAL_PRECISION),
          ]
        );
      }
      return null;
    });

    return {
      t,
      gridStore,
      rasterDatasetStore,
      color,
      toolIsActive,
      drawIsActive,
      anchorStart,
      setLMap,
      cancelToolActive,
      disableContextMenu,
      toggleToolIsActive,
      roundLatLng,
      updateBBox,
      handleMouseDown,
      handleMapMouseMove,
      handleMouseUp,
      keepLatLongInsideBounds,
      outerBounds,
      drawingToolCursor,
    };
  },
});
</script>
<style scoped>
.delete-button {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  padding: 0 !important;
}

.active-button {
  background: rgb(var(--v-theme-primary-lighten3)) !important;
}
</style>
